import { Icons } from '../../components/icons/icon-set';
import { verFull } from '../../data/php-versions';
import { VirtualizedOutput, VIRTUALIZE_THRESHOLD } from '../output/virtualized-output';
import type { RunDisplay } from './result-types';

interface TerminalProps {
  result?: RunDisplay;
  running?: boolean;
}

/** Console output for one version. Prior output stays visible during a re-run
 * (clearing would collapse the pane and cause a jump); swapped in place on land. */
export function Terminal({ result, running }: TerminalProps) {
  if (!result) {
    if (running) {
      return (
        <div class="term-empty">
          <span class="statusdot-spin" />
        </div>
      );
    }
    return (
      <div class="term-empty">
        <div>
          <div class="big">
            <Icons.terminal size={40} />
          </div>
          <div class="sub">
            Press <span class="kbd">▶ Run</span> or <span class="kbd">⌘↵</span> to run the code and see output
            here.
          </div>
        </div>
      </div>
    );
  }
  if (result.lines.length > VIRTUALIZE_THRESHOLD) {
    return <VirtualizedOutput lines={result.lines} />;
  }
  return (
    <div class="term">
      {result.lines.map((l, i) => (
        <div key={i} class={l.c ? 'out-' + l.c : undefined}>
          {l.t === '' ? ' ' : l.t}
        </div>
      ))}
    </div>
  );
}

interface StatusBarProps {
  result?: RunDisplay;
  version: string;
  running?: boolean;
}

/** Time / memory / version / exit chrome under the terminal. */
export function StatusBar({ result, version, running }: StatusBarProps) {
  if (running) {
    return (
      <div class="statusbar">
        <span class="stat">
          <span class="statusdot-spin" /> Running…
        </span>
        <span class="statusbar-sp" />
        <span class="stat">
          PHP <b style={{ fontFamily: 'var(--font-mono)' }}>{verFull(version)}</b>
        </span>
      </div>
    );
  }
  if (!result) {
    return (
      <div class="statusbar">
        <span class="stat out-muted" style={{ color: 'var(--text-faint)' }}>
          Ready
        </span>
      </div>
    );
  }
  return (
    <div class="statusbar">
      <span class="stat">
        <Icons.clock size={13} /> <b>{result.timeMs}</b> ms
      </span>
      <span class="stat">
        <Icons.mem size={13} /> <b>{result.memMB}</b> MB
      </span>
      <span class="statusbar-sp" />
      <span class="stat">
        PHP <b style={{ fontFamily: 'var(--font-mono)' }}>{verFull(version)}</b>
      </span>
      <span class="stat">
        exit <span class={result.exitCode === 0 ? 'exit-ok' : 'exit-err'}>{result.exitCode}</span>
      </span>
    </div>
  );
}
